import { BOOKING_DEFAULT_VISIBLE_COLUMN_IDS } from "./bookingLabels";
import {
  DATE_KEYS,
  TOKEN_KEYS,
  WEIGHT_KEYS,
  parsePositiveInt,
} from "./bookingReportOptions";

/** Flat filter bag captured from the bookings table or built in the sheet. */
export type ReportFilters = Record<string, string | number | boolean | undefined>;

export interface BookingReportFormState {
  reportName: string;
  groupBy: string;
  selectedColumnIds: string[];
  filterTokens: Record<string, string[]>;
  filterDates: Record<string, string>;
  filterWeights: Record<string, string>;
  etdWindow: string;
  etaWindow: string;
  lastUpdatedWindow: string;
  openDateWindow: string;
  tagTriggeredWithinDays: string;
  lastUpdatedWithinDays: string;
  showLocodes: boolean;
  showWeekNumbers: boolean;
  favoritesOnly: boolean;
  /**
   * For delay-oriented reports: hide bookings whose Focus raw ETD has already
   * been updated to match the delay's revised "new" ETD (ops acknowledged the
   * slip in Focus). Default `true`. Maps to the backend opt-out config key
   * `include_focus_acknowledged_delays` (written only when `false`).
   */
  hideFocusAcknowledgedDelays: boolean;
  freezeHeaders: boolean;
  freezeFirstColumn: boolean;
  dedupOn: string[];
  sortBy: string;
  sortDir: string;
  groupSortDir: string;
  scheduleEnabled: boolean;
  exposeRole: string;
  userScoped: boolean;
}

export function emptyFormState(overrides: Partial<BookingReportFormState> = {}): BookingReportFormState {
  const definedOverrides = Object.fromEntries(
    Object.entries(overrides).filter(([, value]) => value !== undefined),
  ) as Partial<BookingReportFormState>;

  return {
    reportName: "",
    groupBy: "",
    selectedColumnIds: [...BOOKING_DEFAULT_VISIBLE_COLUMN_IDS],
    filterTokens: {},
    filterDates: {},
    filterWeights: {},
    etdWindow: "",
    etaWindow: "",
    lastUpdatedWindow: "",
    openDateWindow: "",
    tagTriggeredWithinDays: "",
    lastUpdatedWithinDays: "",
    showLocodes: true,
    showWeekNumbers: true,
    favoritesOnly: false,
    hideFocusAcknowledgedDelays: true,
    freezeHeaders: true,
    freezeFirstColumn: false,
    dedupOn: [],
    sortBy: "departure_date",
    sortDir: "asc",
    groupSortDir: "asc",
    scheduleEnabled: false,
    exposeRole: "",
    userScoped: false,
    ...definedOverrides,
  };
}

function stringFromConfig(cfg: Record<string, unknown>, key: string): string {
  return typeof cfg[key] === "string" ? (cfg[key] as string) : "";
}

/** Whether the workbook layout section should start expanded in the report editor. */
export function workbookLayoutSectionDefaultOpen(
  form: Pick<BookingReportFormState, "freezeHeaders" | "freezeFirstColumn">,
): boolean {
  return !form.freezeHeaders || form.freezeFirstColumn;
}

export function formStateFromConfig(
  cfg: Record<string, unknown>,
): Partial<BookingReportFormState> {
  const tokens: Record<string, string[]> = {};
  for (const key of TOKEN_KEYS) {
    if (typeof cfg[key] === "string" && (cfg[key] as string).length > 0) {
      tokens[key] = (cfg[key] as string)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
    }
  }
  const dates: Record<string, string> = {};
  for (const key of DATE_KEYS) {
    if (typeof cfg[key] === "string" && (cfg[key] as string).length > 0) {
      dates[key] = cfg[key] as string;
    }
  }
  const weights: Record<string, string> = {};
  for (const key of WEIGHT_KEYS) {
    const v = cfg[key];
    if (typeof v === "number") weights[key] = String(v);
    else if (typeof v === "string" && v.length > 0) weights[key] = v;
  }

  return {
    reportName: stringFromConfig(cfg, "name"),
    groupBy: stringFromConfig(cfg, "group_by"),
    selectedColumnIds:
      Array.isArray(cfg.columns) && (cfg.columns as unknown[]).length > 0
        ? (cfg.columns as string[])
        : [...BOOKING_DEFAULT_VISIBLE_COLUMN_IDS],
    filterTokens: tokens,
    filterDates: dates,
    filterWeights: weights,
    etdWindow: stringFromConfig(cfg, "etd_window"),
    etaWindow: stringFromConfig(cfg, "eta_window"),
    lastUpdatedWindow: stringFromConfig(cfg, "last_updated_window"),
    openDateWindow: stringFromConfig(cfg, "open_date_window"),
    tagTriggeredWithinDays:
      cfg.tag_triggered_within_days != null
        ? String(cfg.tag_triggered_within_days)
        : "",
    lastUpdatedWithinDays:
      cfg.last_updated_within_days != null
        ? String(cfg.last_updated_within_days)
        : "",
    showLocodes: cfg.show_locodes === true,
    showWeekNumbers: cfg.show_week_numbers !== false,
    favoritesOnly:
      cfg.favorites_only === true ||
      cfg.favorites_only === "true" ||
      cfg.favorites_only === 1,
    // Hide acknowledged delays unless the saved config explicitly opted in to
    // keep them (`include_focus_acknowledged_delays: true`).
    hideFocusAcknowledgedDelays:
      cfg.include_focus_acknowledged_delays !== true &&
      cfg.include_focus_acknowledged_delays !== "true" &&
      cfg.include_focus_acknowledged_delays !== 1,
    freezeHeaders: cfg.freeze_headers !== false,
    freezeFirstColumn: cfg.freeze_first_column === true,
    sortBy: stringFromConfig(cfg, "sort_by") || "departure_date",
    sortDir: stringFromConfig(cfg, "sort_dir") || "asc",
    groupSortDir: stringFromConfig(cfg, "group_sort_dir") || "asc",
    dedupOn: Array.isArray(cfg.dedup_on)
      ? (cfg.dedup_on as unknown[]).filter((v): v is string => typeof v === "string")
      : [],
    exposeRole: stringFromConfig(cfg, "expose_role"),
    userScoped: cfg.user_scoped === true,
    scheduleEnabled: true,
  };
}

function skipStaticDate(
  key: string,
  windows: { etd: string; eta: string; lastUpdated: string; openDate: string },
): boolean {
  if (windows.etd && (key === "departure_from" || key === "departure_to")) return true;
  if (windows.eta && (key === "eta_from" || key === "eta_to")) return true;
  if (
    windows.lastUpdated &&
    (key === "last_updated_from" || key === "last_updated_to")
  ) {
    return true;
  }
  if (windows.openDate && (key === "open_date_from" || key === "open_date_to")) {
    return true;
  }
  return false;
}

export function buildBookingReportConfig(
  form: BookingReportFormState,
  capturedFilters?: ReportFilters,
): Record<string, unknown> {
  const cfg: Record<string, unknown> = {};
  cfg.name = form.reportName.trim();

  const windows = {
    etd: form.etdWindow,
    eta: form.etaWindow,
    lastUpdated: form.lastUpdatedWindow,
    openDate: form.openDateWindow,
  };

  if (capturedFilters) {
    for (const [key, value] of Object.entries(capturedFilters)) {
      if (value === undefined || value === null || value === "") continue;
      if (skipStaticDate(key, windows)) continue;
      cfg[key] = String(value);
    }
  } else {
    for (const [key, values] of Object.entries(form.filterTokens)) {
      if (values.length > 0) cfg[key] = values.join(",");
    }
    for (const [key, value] of Object.entries(form.filterDates)) {
      if (!value || skipStaticDate(key, windows)) continue;
      cfg[key] = value;
    }
    for (const key of ["weight_gte", "weight_lte", "weight_eq"]) {
      const raw = form.filterWeights[key];
      if (raw == null || raw.trim() === "") continue;
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) cfg[key] = String(n);
    }
    for (const key of [
      "total_containers_gte",
      "total_containers_lte",
      "total_containers_eq",
      "delay_days_gte",
      "delay_days_lte",
      "delay_days_eq",
      "eta_delay_days_gte",
      "eta_delay_days_lte",
      "eta_delay_days_eq",
    ]) {
      const raw = form.filterWeights[key];
      if (raw == null || raw.trim() === "") continue;
      const n = Number(raw);
      if (Number.isInteger(n) && n >= 0) cfg[key] = String(n);
    }
  }

  if (form.etdWindow) cfg.etd_window = form.etdWindow;
  if (form.etaWindow) cfg.eta_window = form.etaWindow;
  if (form.openDateWindow) cfg.open_date_window = form.openDateWindow;

  const tagsSelected = capturedFilters
    ? Boolean(capturedFilters.tag)
    : (form.filterTokens.tag?.length ?? 0) > 0;
  const tagDays = parsePositiveInt(form.tagTriggeredWithinDays);
  if (tagDays !== null && tagsSelected) {
    cfg.tag_triggered_within_days = tagDays;
  }

  if (form.lastUpdatedWindow) {
    cfg.last_updated_window = form.lastUpdatedWindow;
  } else {
    const lastUpdatedDays = parsePositiveInt(form.lastUpdatedWithinDays);
    if (lastUpdatedDays !== null) cfg.last_updated_within_days = lastUpdatedDays;
  }

  if (form.groupBy) cfg.group_by = form.groupBy;
  if ((form.selectedColumnIds?.length ?? 0) > 0) {
    cfg.columns = form.selectedColumnIds;
  }
  cfg.show_locodes = form.showLocodes;
  cfg.show_week_numbers = form.showWeekNumbers;
  if (form.favoritesOnly) {
    cfg.favorites_only = true;
  } else {
    delete cfg.favorites_only;
  }
  // Delay-report noise control: the backend hides Focus-acknowledged delay rows
  // by default, so only persist the opt-out key when the user chose to keep them.
  if (!form.hideFocusAcknowledgedDelays) {
    cfg.include_focus_acknowledged_delays = true;
  } else {
    delete cfg.include_focus_acknowledged_delays;
  }
  cfg.freeze_headers = form.freezeHeaders;
  cfg.freeze_first_column = form.freezeFirstColumn;
  cfg.sort_by = form.sortBy;
  cfg.sort_dir = form.sortDir;
  if (form.groupBy) cfg.group_sort_dir = form.groupSortDir;
  if ((form.dedupOn?.length ?? 0) > 0) cfg.dedup_on = form.dedupOn;
  if (form.exposeRole) cfg.expose_role = form.exposeRole;
  // Per-recipient delivery: explicit split toggle, or implicit for starred-only
  // schedules (each recipient's own favorites; empty → no email).
  if (
    form.scheduleEnabled &&
    (form.userScoped || form.favoritesOnly)
  ) {
    cfg.user_scoped = true;
  }

  return cfg;
}

export function capturedFilterEntries(
  filters: ReportFilters,
  windows: { etd: string; eta: string; lastUpdated: string; openDate: string },
  labels: Record<string, string>,
): { key: string; label: string; value: string }[] {
  const entries: { key: string; label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    if (skipStaticDate(key, windows)) continue;
    entries.push({
      key,
      label: labels[key] ?? key,
      value: String(value),
    });
  }
  return entries;
}

const REPORT_LAYOUT_KEYS = new Set([
  "name",
  "group_by",
  "columns",
  "show_locodes",
  "show_week_numbers",
  "favorites_only",
  "include_focus_acknowledged_delays",
  "freeze_headers",
  "freeze_first_column",
  "dedup_on",
  "sort_by",
  "sort_dir",
  "group_sort_dir",
  "expose_role",
  "user_scoped",
]);

const WINDOW_VALUE_LABELS: Record<string, string> = {
  this_week: "This week",
  last_week: "Last week",
  next_week: "Next week",
  this_month: "This month",
  next_month: "Next month",
  next_30_days: "Next 30 days",
  next_60_days: "Next 60 days",
  next_90_days: "Next 90 days",
  today: "Today",
  yesterday: "Yesterday",
  last_7_days: "Last 7 days",
  last_14_days: "Last 14 days",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
};

function displayFilterValue(key: string, raw: string): string {
  if (
    key.endsWith("_window") ||
    key === "etd_window" ||
    key === "eta_window" ||
    key === "open_date_window" ||
    key === "last_updated_window"
  ) {
    return WINDOW_VALUE_LABELS[raw] ?? raw.replaceAll("_", " ");
  }
  return raw;
}

/**
 * Read-only filter list from a Kai / saved booking-report config so the
 * download card can audit what the file was filtered by.
 */
export function reportConfigFilterEntries(
  config: Record<string, unknown> | null | undefined,
  labels: Record<string, string>,
): { key: string; label: string; value: string }[] {
  if (!config) return [];
  const windows = {
    etd: stringFromConfig(config, "etd_window"),
    eta: stringFromConfig(config, "eta_window"),
    lastUpdated: stringFromConfig(config, "last_updated_window"),
    openDate: stringFromConfig(config, "open_date_window"),
  };
  const entries: { key: string; label: string; value: string }[] = [];
  for (const [key, value] of Object.entries(config)) {
    if (REPORT_LAYOUT_KEYS.has(key)) continue;
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "boolean" || Array.isArray(value)) continue;
    if (skipStaticDate(key, windows)) continue;
    const raw = String(value).trim();
    if (!raw) continue;
    entries.push({
      key,
      label: labels[key] ?? key.replaceAll("_", " "),
      value: displayFilterValue(key, raw),
    });
  }
  return entries;
}
